import {
  createFileRoute,
  useNavigate,
  useLocation,
} from "@tanstack/react-router";
import { useQuery, useMutation, useAction, useConvexAuth } from "convex/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../convex/_generated/api";
import { type Id } from "../../../convex/_generated/dataModel";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/macro";
import { Crown, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import type { TemplateSectionId } from "../../../convex/templateTypes";
import type { TemplateTextMode } from "../../../convex/templateTextEditUtils";

import Header from "../../components/Header";
import DesktopHeaderTabs from "../../components/admin/DesktopHeaderTabs";
import EditSidebar from "../../components/admin/EditSidebar";
import ElementEditPopup from "../../components/admin/ElementEditPopup";
import MobileTabBar from "../../components/admin/MobileTabBar";
import PremiumBenefitsPopup from "../../components/admin/PremiumBenefitsPopup";
import PreviewEditMenu from "../../components/admin/PreviewEditMenu";
import SlugEditor from "../../components/admin/SlugEditor";
import TemplateTextEditPopup from "../../components/admin/TemplateTextEditPopup";
import { useLocalizedPrice } from "../../hooks/useLocalizedPrice";
import { exportRsvpsToCsv } from "../../lib/exportRsvpsToCsv";
import { injectIframeAnchorGuards } from "../../lib/iframeAnchorGuards";
import { getPublicSiteOrigin } from "../../utils/publicSiteHost";

export const Route = createFileRoute("/admin/$slug")({
  component: AdminPage,
});

/**
 * Returns true when the viewport is ≥ 1024px (Tailwind's `lg` breakpoint).
 * Used to ensure only ONE preview iframe is mounted at a time so the
 * iframeRef always points to the visible iframe.
 */
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 1024px)").matches
      : true,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

/**
 * Inject a postMessage listener into the generated HTML so the admin preview
 * can receive live CSS variable updates from the StyleEditor, element
 * inspection for the visual editor, and scroll position tracking.
 */
function injectPreviewScript(html: string | undefined): string {
  if (!html) {
    return "";
  }
  const script = String.raw`<script>
// ── Style overrides + scroll ──
window.addEventListener('message', function(e) {
  if (!e.data) return;
  if (e.data.type === 'styleOverrides') {
    var vars = e.data.variables;
    for (var key in vars) {
      document.documentElement.style.setProperty(key, vars[key]);
    }
    if (e.data.fontsUrl) {
      var existing = document.getElementById('bodaweb-preview-fonts');
      if (!existing || existing.getAttribute('href') !== e.data.fontsUrl) {
        var link = document.createElement('link');
        link.id = 'bodaweb-preview-fonts';
        link.rel = 'stylesheet';
        link.href = e.data.fontsUrl;
        if (existing) existing.remove();
        document.head.appendChild(link);
      }
    }
  }
  if (e.data.type === 'scrollRestore') {
    window.scrollTo(0, e.data.scrollY);
  }

  // ── Edit mode ──
  if (e.data.type === 'setEditMode') {
    _editMode = !!e.data.enabled;
    if (!_editMode) {
      _clearPendingTouchSelection();
      _hideOverlay();
      _hideTooltip();
    }
  }
  if (e.data.type === 'clearSelection') {
    _clearPendingTouchSelection();
    _hideOverlay();
    _hideTooltip();
  }
  if (e.data.type === 'replaceElement') {
    try {
      var el = document.querySelector(e.data.elementPath);
      if (el) {
        var tmp = document.createElement('div');
        tmp.innerHTML = e.data.newHtml;
        el.replaceWith(tmp.firstElementChild || tmp.firstChild);
      }
    } catch(ex) {}
  }
});

var _scrollTimer;
window.addEventListener('scroll', function() {
  clearTimeout(_scrollTimer);
  if (_pendingTouchTarget) {
    _clearPendingTouchSelection();
    _hideOverlay();
    _hideTooltip();
  }
  _scrollTimer = setTimeout(function() {
    window.parent.postMessage({ type: 'iframeScroll', scrollY: window.scrollY }, '*');
  }, 50);
});

// ── Element inspection ──
var _editMode = false;
var _highlightEl = null;
var _pendingTouchTarget = null;
var _lastTouchSelectionAt = 0;

// Create highlight overlay (thin dashed outline, transparent fill)
var _overlay = document.createElement('div');
_overlay.style.cssText = 'position:fixed;pointer-events:none;z-index:99999;border:1px dashed var(--color-accent,#3b82f6);background:transparent;display:none;transition:top 0.05s,left 0.05s,width 0.05s,height 0.05s';
_overlay.setAttribute('data-bodaweb-selection-overlay', 'true');
document.body.appendChild(_overlay);

// "Editar" chip anchored to the top-right of the overlay for editable targets.
var _chip = document.createElement('div');
_chip.style.cssText = 'position:absolute;top:0;right:-1px;transform:translateY(-100%);padding:2px 6px;background:var(--color-accent,#3b82f6);color:white;font-size:10px;font-family:system-ui,sans-serif;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;white-space:nowrap;pointer-events:none;display:none';
_chip.setAttribute('data-bodaweb-selection-chip', 'true');
// Hard-coded Spanish — this string lives inside the preview iframe's srcDoc and
// can't be processed by Lingui's JSX/t-macro extractor.
_chip.textContent = 'Editar';
_overlay.appendChild(_chip);

// Create "not editable" tooltip
var _tooltip = document.createElement('div');
_tooltip.style.cssText = 'position:fixed;pointer-events:none;z-index:100000;padding:4px 10px;background:#6b7280;color:white;font-size:12px;border-radius:4px;display:none;font-family:system-ui,sans-serif;white-space:nowrap';
_tooltip.textContent = 'No editable';
_tooltip.setAttribute('data-bodaweb-selection-tooltip', 'true');
document.body.appendChild(_tooltip);

function _hideOverlay() {
  _overlay.style.display = 'none';
  _highlightEl = null;
}
function _hideTooltip() {
  _tooltip.style.display = 'none';
}

function _showTooltip(rect, text) {
  _tooltip.textContent = text;
  _tooltip.style.top = (rect.top - 28) + 'px';
  _tooltip.style.left = rect.left + 'px';
  _tooltip.style.display = 'block';
}

function _getSectionAncestor(el) {
  return el.closest('[data-section-type]');
}

function _getTemplateEditableTarget(el) {
  return el.closest('[data-bodaweb-edit-id]');
}

function _isSkippable(el) {
  var tag = el.tagName;
  return tag === 'HTML' || tag === 'BODY' || tag === 'HEAD';
}

function _readEditableText(el) {
  var text = el.innerText || el.textContent || '';
  return text.replace(/\\r\\n?/g, '\\n').replace(/\\u00a0/g, ' ').trim();
}

function _buildSelectorPath(el) {
  var parts = [];
  var cur = el;
  while (cur && cur.nodeType === 1) {
    var sec = cur.getAttribute('data-section-id');
    if (sec) {
      parts.unshift('[data-section-id="' + sec + '"]');
      break;
    }
    var tag = cur.tagName.toLowerCase();
    var parent = cur.parentElement;
    if (parent) {
      var siblings = Array.from(parent.children);
      var sameTag = siblings.filter(function(s) { return s.tagName === cur.tagName; });
      if (sameTag.length > 1) {
        var idx = sameTag.indexOf(cur) + 1;
        tag += ':nth-of-type(' + idx + ')';
      }
    }
    parts.unshift(tag);
    cur = parent;
  }
  return parts.join(' > ');
}

function _stripSectionAttrs(html) {
  return html.replace(/ data-section-(?:type|id)="[^"]*"/g, '');
}

function _clearPendingTouchSelection() {
  _pendingTouchTarget = null;
}

function _isMobileViewport() {
  return window.matchMedia('(max-width: 1023px)').matches;
}

function _getSelectionDetails(target) {
  if (!target || _isSkippable(target)) return null;

  var sec = _getSectionAncestor(target);
  if (!sec) return null;

  var sType = sec.getAttribute('data-section-type');
  if (sType === 'template') {
    var editable = _getTemplateEditableTarget(target);
    var highlightTarget = editable || target;
    var rect = highlightTarget.getBoundingClientRect();
    return {
      editable: editable,
      highlightTarget: highlightTarget,
      rect: rect,
      section: sec,
      sectionType: sType
    };
  }

  if (sType === 'custom') {
    var customRect = target.getBoundingClientRect();
    return {
      highlightTarget: target,
      rect: customRect,
      section: sec,
      sectionType: sType
    };
  }

  return null;
}

function _showSelectionOutline(details) {
  if (!details) {
    _hideOverlay();
    _hideTooltip();
    return;
  }

  var rect = details.rect;
  _overlay.style.top = rect.top + 'px';
  _overlay.style.left = rect.left + 'px';
  _overlay.style.width = rect.width + 'px';
  _overlay.style.height = rect.height + 'px';
  _overlay.style.display = 'block';
  _highlightEl = details.highlightTarget;

  if (details.sectionType === 'template' && !details.editable) {
    _overlay.style.borderColor = '#9ca3af';
    _chip.style.display = 'none';
    _showTooltip(rect, 'No editable');
    return;
  }

  _overlay.style.borderColor = 'var(--color-accent,#3b82f6)';
  _chip.style.display = 'block';
  _hideTooltip();
}

function _postSelection(details) {
  if (!details) return;

  if (details.sectionType === 'template') {
    if (!details.editable) {
      window.parent.postMessage({
        type: 'elementNotEditable',
        elementRect: { top: details.rect.top, left: details.rect.left, width: details.rect.width, height: details.rect.height }
      }, '*');
      return;
    }

    window.parent.postMessage({
      type: 'elementSelected',
      editorKind: 'templateText',
      sectionId: details.section.getAttribute('data-section-id'),
      editableId: details.editable.getAttribute('data-bodaweb-edit-id'),
      currentText: _readEditableText(details.editable),
      textMode: details.editable.getAttribute('data-bodaweb-text-mode') === 'multiline' ? 'multiline' : 'singleline',
      elementRect: { top: details.rect.top, left: details.rect.left, width: details.rect.width, height: details.rect.height },
      iframeScrollY: window.scrollY
    }, '*');
    return;
  }

  if (details.sectionType === 'custom') {
    var selectorPath = _buildSelectorPath(details.highlightTarget);
    var html = details.highlightTarget === details.section
      ? _stripSectionAttrs(details.highlightTarget.outerHTML)
      : details.highlightTarget.outerHTML;
    window.parent.postMessage({
      type: 'elementSelected',
      editorKind: 'customAi',
      sectionId: details.section.getAttribute('data-section-id'),
      elementPath: selectorPath,
      elementOuterHtml: html,
      elementRect: { top: details.rect.top, left: details.rect.left, width: details.rect.width, height: details.rect.height },
      iframeScrollY: window.scrollY
    }, '*');
  }
}

document.addEventListener('mousemove', function(e) {
  if (!_editMode) return;
  if (_isMobileViewport()) return;
  _showSelectionOutline(_getSelectionDetails(e.target));
}, { passive: true });

document.addEventListener('mouseleave', function() {
  if (_editMode && !_pendingTouchTarget) { _hideOverlay(); _hideTooltip(); }
});

document.addEventListener('click', function(e) {
  if (!_editMode) return;
  if (Date.now() - _lastTouchSelectionAt < 500) return;

  var details = _getSelectionDetails(e.target);
  if (!details) return;

  e.preventDefault();
  e.stopPropagation();
  _clearPendingTouchSelection();
  _postSelection(details);
}, true);

// Touch support for mobile
document.addEventListener('touchend', function(e) {
  if (!_editMode) return;
  var touch = e.changedTouches[0];
  if (!touch) return;
  var target = document.elementFromPoint(touch.clientX, touch.clientY);
  var details = _getSelectionDetails(target);
  if (!details) {
    _clearPendingTouchSelection();
    _hideOverlay();
    _hideTooltip();
    return;
  }

  e.preventDefault();

  _lastTouchSelectionAt = Date.now();
  _showSelectionOutline(details);

  if (!_isMobileViewport()) {
    _clearPendingTouchSelection();
    _hideTooltip();
    _postSelection(details);
    return;
  }

  if (_pendingTouchTarget === details.highlightTarget) {
    _clearPendingTouchSelection();
    _hideTooltip();
    _postSelection(details);
    return;
  }

  // First tap on a new target: keep the outline + "Editar" chip visible
  // (already rendered by _showSelectionOutline above) so the first-tap
  // affordance on touch devices mirrors the desktop hover preview.
  // The second tap on the same target posts the selection (handled above).
  _pendingTouchTarget = details.highlightTarget;
}, { passive: false });

</script>`;
  return injectIframeAnchorGuards(html.replace("</body>", script + "</body>"), {
    blockNonAnchorLinks: true,
    editModeEnabledVariableName: "_editMode",
    preventForms: false,
  });
}

type DesktopTab = "edit" | "rsvp";
type MobileTab = "preview" | "edit" | "rsvp";

type SelectedCustomAiElement = {
  editorKind: "customAi";
  elementHtml: string;
  elementPath: string;
  elementRect: { height: number; left: number; top: number; width: number };
  iframeScrollY: number;
  sectionId: string;
};

type SelectedTemplateTextElement = {
  currentText: string;
  editableId: string;
  editorKind: "templateText";
  elementRect: { height: number; left: number; top: number; width: number };
  iframeScrollY: number;
  sectionId: TemplateSectionId;
  textMode: TemplateTextMode;
};

type SelectedElement = SelectedCustomAiElement | SelectedTemplateTextElement;

const SIDEBAR_COLLAPSED_STORAGE_KEY = "bodaweb.admin.sidebarCollapsed";

function readSidebarCollapsedState(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
}

interface RsvpEntry {
  _id: string;
  additionalInfo?: string;
  attending: boolean;
  guestName: string;
  numberOfGuests: number;
  submittedAt: number;
}

type ConfirmAction =
  | { ids: Array<string>; type: "delete" }
  | { attending: boolean; ids: Array<string>; type: "toggleAttending" }
  | { ids: Array<string>; numberOfGuests: number; type: "updateGuests" };

interface RsvpViewProps {
  isLimited: boolean;
  isSubscribed: boolean;
  onShowPremiumBenefits: () => void;
  rsvps: RsvpEntry[] | undefined;
  siteSlug: string;
  totalCount: number;
  totals: {
    totalAttending: number;
    totalDeclined: number;
    totalGuests: number;
  };
}

function RsvpView({
  isLimited,
  isSubscribed,
  onShowPremiumBenefits,
  rsvps,
  siteSlug,
  totalCount,
  totals,
}: RsvpViewProps) {
  const { totalAttending, totalDeclined, totalGuests } = totals;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(
    null,
  );
  const [guestsInput, setGuestsInput] = useState(1);
  const bulkUpdate = useMutation(api.rsvp.bulkUpdateRsvps);
  const deleteRsvps = useMutation(api.rsvp.deleteRsvps);

  // Clear selection when rsvps change (e.g. after deletion)
  useEffect(() => {
    setSelectedIds((prev) => {
      if (!rsvps) {
        return prev;
      }
      const currentIds = new Set(rsvps.map((r) => r._id));
      const next = new Set([...prev].filter((id) => currentIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rsvps]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!rsvps) {
      return;
    }
    if (selectedIds.size === rsvps.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rsvps.map((r) => r._id)));
    }
  };

  const selectedRsvps = rsvps?.filter((r) => selectedIds.has(r._id)) ?? [];
  const allSelectedAttending =
    selectedRsvps.length > 0 && selectedRsvps.every((r) => r.attending);
  const allSelectedDeclined =
    selectedRsvps.length > 0 && selectedRsvps.every((r) => !r.attending);

  const handleConfirm = async () => {
    if (!confirmAction) {
      return;
    }
    const ids = confirmAction.ids as Array<Id<"rsvpResponses">>;
    if (confirmAction.type === "delete") {
      await deleteRsvps({ rsvpIds: ids });
    } else if (confirmAction.type === "toggleAttending") {
      await bulkUpdate({ attending: confirmAction.attending, rsvpIds: ids });
    } else if (confirmAction.type === "updateGuests") {
      await bulkUpdate({
        numberOfGuests: confirmAction.numberOfGuests,
        rsvpIds: ids,
      });
    }
    setSelectedIds(new Set());
    setConfirmAction(null);
  };

  const openDeleteConfirm = () => {
    setConfirmAction({ ids: [...selectedIds], type: "delete" });
  };

  const openToggleConfirm = (attending: boolean) => {
    setConfirmAction({
      attending,
      ids: [...selectedIds],
      type: "toggleAttending",
    });
  };

  const openUpdateGuestsConfirm = () => {
    setGuestsInput(1);
    setConfirmAction({
      ids: [...selectedIds],
      numberOfGuests: 1,
      type: "updateGuests",
    });
  };

  // Dialog content helpers
  const getSingleName = (ids: Array<string>) => {
    if (ids.length !== 1) {
      return null;
    }
    return rsvps?.find((r) => r._id === ids[0])?.guestName ?? null;
  };

  const getDialogTitle = () => {
    if (!confirmAction) {
      return "";
    }
    const count = confirmAction.ids.length;
    const name = getSingleName(confirmAction.ids);
    if (confirmAction.type === "delete") {
      return name ? t`¿Eliminar a ${name}?` : t`¿Eliminar ${count} invitados?`;
    }
    if (confirmAction.type === "toggleAttending") {
      if (confirmAction.attending) {
        return name
          ? t`¿Marcar a ${name} como asistirá?`
          : t`¿Marcar ${count} como asistirá?`;
      }
      return name
        ? t`¿Marcar a ${name} como no asistirá?`
        : t`¿Marcar ${count} como no asistirá?`;
    }
    return name
      ? t`¿Actualizar invitados para ${name}?`
      : t`¿Actualizar invitados para ${count} registros?`;
  };

  const getDialogDescription = () => {
    if (!confirmAction) {
      return "";
    }
    if (confirmAction.type === "delete") {
      return t`Esta acción no se puede deshacer.`;
    }
    if (confirmAction.type === "toggleAttending") {
      return confirmAction.attending
        ? t`Se marcará como confirmado.`
        : t`Se marcará como no asistirá.`;
    }
    return t`Se actualizará el número de invitados.`;
  };

  return (
    <div className="space-y-6">
      {/* RSVP Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="border border-[var(--color-border)] bg-white p-4 text-center">
          <div
            className="text-2xl font-bold text-[var(--color-accent)]"
            data-testid="rsvp-confirmed"
          >
            {totalAttending}
          </div>
          <div className="text-sm text-[var(--color-muted)]">
            <Trans>Confirmados</Trans>
          </div>
        </div>
        <div className="border border-[var(--color-border)] bg-white p-4 text-center">
          <div
            className="text-2xl font-bold text-[var(--color-danger)]"
            data-testid="rsvp-declined"
          >
            {totalDeclined}
          </div>
          <div className="text-sm text-[var(--color-muted)]">
            <Trans>No asistirán</Trans>
          </div>
        </div>
        <div className="border border-[var(--color-border)] bg-white p-4 text-center">
          <div
            className="text-2xl font-bold text-[var(--color-ink)]"
            data-testid="rsvp-total-guests"
          >
            {totalGuests}
          </div>
          <div className="text-sm text-[var(--color-muted)]">
            <Trans>Total invitados</Trans>
          </div>
        </div>
      </div>

      {/* RSVP Table */}
      <div className="overflow-hidden border border-[var(--color-border)] bg-white">
        <div className="flex items-center justify-between gap-2 border-b border-[var(--color-border)] px-4 py-3">
          <h2 className="font-semibold text-[var(--color-ink)]">
            <Trans>Confirmaciones</Trans>
          </h2>
          <button
            className="inline-flex cursor-pointer items-center gap-1.5 border border-[var(--color-border)] bg-[var(--color-page-strong)] px-3 py-1.5 text-xs text-[var(--color-ink)] transition-colors hover:border-[var(--color-border-strong)] hover:bg-[rgba(255,253,248,0.98)] disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="rsvp-export-csv"
            disabled={isSubscribed && (!rsvps || rsvps.length === 0)}
            onClick={() => {
              if (!isSubscribed) {
                onShowPremiumBenefits();
                return;
              }
              exportRsvpsToCsv(rsvps ?? [], siteSlug);
            }}
          >
            {!isSubscribed && (
              <Crown aria-hidden="true" className="h-3.5 w-3.5" />
            )}
            <Trans>Exportar CSV</Trans>
          </button>
        </div>

        {isLimited && (
          <div
            className="flex flex-col gap-2 border-b border-[var(--color-border)] bg-[var(--color-accent-soft)] px-4 py-3 text-sm text-[var(--color-accent-strong)] sm:flex-row sm:items-center"
            data-testid="rsvp-limit-banner"
          >
            <span className="sm:mr-auto">
              <Trans>
                Estás viendo las primeras 25 de {totalCount} confirmaciones.
                Suscríbete a Premium para verlas todas.
              </Trans>
            </span>
            <button
              className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 bg-[var(--color-accent)] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[var(--color-accent-strong)]"
              data-testid="rsvp-limit-banner-subscribe"
              onClick={onShowPremiumBenefits}
              type="button"
            >
              <Crown aria-hidden="true" className="h-3.5 w-3.5" />
              <Trans>Suscribirse</Trans>
            </button>
          </div>
        )}

        {/* Action toolbar — visible when items selected */}
        {selectedIds.size > 0 && (
          <div
            className="flex flex-col gap-2 border-b border-[var(--color-border)] bg-[rgba(220,230,223,0.52)] px-4 py-2 sm:flex-row sm:items-center"
            data-testid="rsvp-action-bar"
          >
            <span className="text-sm font-medium text-[var(--color-accent-strong)] sm:mr-auto">
              {selectedIds.size} <Trans>seleccionado(s)</Trans>
            </span>
            <div className="flex flex-wrap items-center gap-2">
              {!allSelectedDeclined && (
                <button
                  className="cursor-pointer border border-[rgba(170,83,63,0.26)] bg-[var(--color-page-strong)] px-3 py-1.5 text-xs text-[var(--color-danger)] transition-colors hover:bg-[var(--color-danger-soft)]"
                  data-testid="rsvp-action-toggle-status"
                  onClick={() => openToggleConfirm(false)}
                >
                  <Trans>Marcar no asistirá</Trans>
                </button>
              )}
              {!allSelectedAttending && (
                <button
                  className="cursor-pointer border border-[var(--color-accent-soft-strong)] bg-[var(--color-page-strong)] px-3 py-1.5 text-xs text-[var(--color-accent-strong)] transition-colors hover:bg-[var(--color-accent-soft)]"
                  data-testid="rsvp-action-toggle-attending"
                  onClick={() => openToggleConfirm(true)}
                >
                  <Trans>Marcar asistirá</Trans>
                </button>
              )}
              <button
                className="cursor-pointer border border-[var(--color-border)] bg-[var(--color-page-strong)] px-3 py-1.5 text-xs text-[var(--color-ink)] transition-colors hover:border-[var(--color-border-strong)] hover:bg-[rgba(255,253,248,0.98)]"
                data-testid="rsvp-action-update-guests"
                onClick={openUpdateGuestsConfirm}
              >
                <Trans>Cambiar invitados</Trans>
              </button>
              <button
                className="cursor-pointer border border-[rgba(170,83,63,0.26)] bg-[var(--color-page-strong)] px-3 py-1.5 text-xs text-[var(--color-danger)] transition-colors hover:bg-[var(--color-danger-soft)]"
                data-testid="rsvp-action-delete"
                onClick={openDeleteConfirm}
              >
                <Trans>Eliminar</Trans>
              </button>
            </div>
          </div>
        )}

        {rsvps && rsvps.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[rgba(235,226,211,0.54)] text-[var(--color-muted)]">
                <tr>
                  <th className="px-4 py-2 w-8">
                    <input
                      checked={
                        rsvps.length > 0 && selectedIds.size === rsvps.length
                      }
                      className="cursor-pointer"
                      data-testid="rsvp-select-all"
                      onChange={toggleSelectAll}
                      type="checkbox"
                    />
                  </th>
                  <th className="text-left px-4 py-2 font-medium">
                    <Trans>Nombre</Trans>
                  </th>
                  <th className="text-left px-4 py-2 font-medium">
                    <Trans>Invitados</Trans>
                  </th>
                  <th className="text-left px-4 py-2 font-medium">
                    <Trans>Asistencia</Trans>
                  </th>
                  <th className="text-left px-4 py-2 font-medium">
                    <Trans>Notas</Trans>
                  </th>
                  <th className="text-left px-4 py-2 font-medium">
                    <Trans>Fecha</Trans>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(216,206,188,0.46)]">
                {rsvps.map((rsvp) => (
                  <tr
                    className={`transition-colors hover:bg-[rgba(220,230,223,0.24)] ${selectedIds.has(rsvp._id) ? "bg-[rgba(220,230,223,0.38)]" : ""}`}
                    key={rsvp._id}
                  >
                    <td className="px-4 py-2.5">
                      <input
                        checked={selectedIds.has(rsvp._id)}
                        className="cursor-pointer"
                        data-testid="rsvp-select-row"
                        onChange={() => toggleSelect(rsvp._id)}
                        type="checkbox"
                      />
                    </td>
                    <td className="px-4 py-2.5">{rsvp.guestName}</td>
                    <td className="px-4 py-2.5" data-testid="rsvp-guest-count">
                      {rsvp.numberOfGuests}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-block px-2.5 py-1 text-xs font-medium ${
                          rsvp.attending
                            ? "bg-[var(--color-accent-soft)] text-[var(--color-accent-strong)]"
                            : "bg-[var(--color-danger-soft)] text-[var(--color-danger)]"
                        }`}
                      >
                        {rsvp.attending ? "Asistirá" : "No asistirá"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-[var(--color-muted)]">
                      {rsvp.additionalInfo || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-[var(--color-muted)]">
                      {new Date(rsvp.submittedAt).toLocaleDateString("es")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-4 py-8 text-center text-sm text-[var(--color-muted)]">
            <Trans>Aún no hay respuestas</Trans>
          </div>
        )}
      </div>

      {/* Confirmation dialog */}
      {confirmAction && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(15,21,18,0.42)]"
          data-testid="rsvp-confirm-dialog"
          onClick={() => setConfirmAction(null)}
        >
          <div
            className="mx-4 w-full max-w-sm border border-[var(--color-border)] bg-[var(--color-page-strong)] p-6 shadow-[0_32px_70px_rgba(15,21,18,0.2)]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 text-lg font-semibold text-[var(--color-ink)]">
              {getDialogTitle()}
            </h3>
            <p className="mb-4 text-sm text-[var(--color-muted)]">
              {getDialogDescription()}
            </p>
            {confirmAction.type === "updateGuests" && (
              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-[var(--color-ink)]">
                  <Trans>Número de invitados</Trans>
                </label>
                <input
                  className="app-input w-full text-sm"
                  data-testid="rsvp-guests-input"
                  min={1}
                  onChange={(e) => {
                    const val = Number.parseInt(e.target.value, 10);
                    if (val > 0) {
                      setGuestsInput(val);
                      setConfirmAction({
                        ...confirmAction,
                        numberOfGuests: val,
                      });
                    }
                  }}
                  type="number"
                  value={guestsInput}
                />
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                className="cursor-pointer px-4 py-2 text-sm text-[var(--color-muted)] transition-colors hover:bg-gray-100 hover:text-[var(--color-ink)]"
                data-testid="rsvp-cancel-action"
                onClick={() => setConfirmAction(null)}
              >
                <Trans>Cancelar</Trans>
              </button>
              <button
                className={`cursor-pointer px-4 py-2 text-sm text-white transition-colors ${
                  confirmAction.type === "delete"
                    ? "bg-[var(--color-danger)] hover:bg-[#8c4333]"
                    : "bg-[var(--color-accent)] hover:bg-[var(--color-accent-strong)]"
                }`}
                data-testid="rsvp-confirm-action"
                onClick={handleConfirm}
              >
                <Trans>Confirmar</Trans>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface AdminActionBarProps {
  currentSlug: string;
  onShowPremiumBenefits: () => void;
  onSlugSaved: (slug: string) => Promise<void> | void;
  showPremium: boolean;
  siteId: Id<"weddingSites">;
  siteUrl: string;
}

function AdminActionBar({
  currentSlug,
  onShowPremiumBenefits,
  onSlugSaved,
  showPremium,
  siteId,
  siteUrl,
}: AdminActionBarProps) {
  return (
    <div
      className="border-b border-gray-100 px-4 py-4 space-y-3"
      data-testid="admin-action-bar"
    >
      <div className="flex items-center gap-2">
        {showPremium && (
          <button
            className="flex flex-1 items-center justify-center gap-2 bg-[var(--color-accent)] px-4 py-2.5 text-sm text-white transition-colors cursor-pointer hover:bg-[var(--color-accent-strong)]"
            data-testid="sidebar-premium-button"
            onClick={onShowPremiumBenefits}
            type="button"
          >
            <Crown aria-hidden="true" className="h-4 w-4" />
            <Trans>Premium</Trans>
          </button>
        )}
        <a
          className={`${showPremium ? "flex-1" : "w-full"} inline-flex items-center justify-center gap-2 border border-[var(--color-border)] px-4 py-2.5 text-sm text-[var(--color-muted)] transition-colors cursor-pointer hover:border-[var(--color-ink)] hover:text-[var(--color-ink)]`}
          href={siteUrl}
          rel="noopener noreferrer"
          target="_blank"
          title={t`Abrir mi invitación`}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" x2="21" y1="14" y2="3" />
          </svg>
          <Trans>Abrir</Trans>
        </a>
      </div>
      <div data-testid="admin-slug-editor">
        <SlugEditor
          currentSlug={currentSlug}
          onSlugSaved={onSlugSaved}
          siteId={siteId}
        />
      </div>
    </div>
  );
}

function AdminPage() {
  const { slug: routeSlug } = Route.useParams();
  const location = useLocation();
  const checkout = new URLSearchParams(location.search).get("checkout");
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();
  const navigate = useNavigate();
  const site = useQuery(api.weddingSites.getBySlug, { slug: routeSlug });
  const rsvpsResult = useQuery(
    api.rsvp.listForSite,
    site?._id ? { weddingSiteId: site._id } : "skip",
  );
  const rsvps = rsvpsResult?.rsvps;
  const rsvpTotalCount = rsvpsResult?.totalCount ?? 0;
  const rsvpIsLimited = rsvpsResult?.isLimited ?? false;
  const photos = useQuery(
    api.photos.listForSitePublic,
    site?._id ? { weddingSiteId: site._id } : "skip",
  );
  const hasRenderablePhotos = photos?.some((photo) => photo.url !== null);
  const subscriptionStatus = useQuery(api.subscriptions.getSubscriptionStatus);
  const templateTextUndoState = useQuery(
    api.templateTextEdits.getUndoState,
    site?._id ? { siteId: site._id } : "skip",
  );
  const createCheckoutSession = useAction(
    api.stripeActions.createCheckoutSession,
  );
  const createPortalSession = useAction(api.stripeActions.createPortalSession);
  const localizedPrice = useLocalizedPrice();

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const scrollPositionRef = useRef(0);
  const [iframeLoadCount, setIframeLoadCount] = useState(0);
  const undoTemplateText = useMutation(api.templateTextEdits.undoLast);
  const [pendingSlug, setPendingSlug] = useState<{
    fromSlug: string;
    slug: string;
  } | null>(null);

  // ── Editing state ──
  // The preview is directly manipulable by default: hovering editable text
  // shows an affordance and clicking it opens the edit popup. Users can
  // toggle "Vista limpia" (clean view) to silence the affordances and view
  // the site as a guest would — that maps to `_editMode = false` inside the
  // iframe script, which was the previous default.
  const [cleanView, setCleanView] = useState(false);
  const [selectedElement, setSelectedElement] =
    useState<SelectedElement | null>(null);
  const [isUndoingTemplateText, setIsUndoingTemplateText] = useState(false);
  const [notEditableToast, setNotEditableToast] = useState(false);
  const cleanViewRef = useRef(false);

  const toggleCleanView = useCallback(() => {
    const next = !cleanViewRef.current;
    cleanViewRef.current = next;
    setCleanView(next);
    setSelectedElement(null);
    iframeRef.current?.contentWindow?.postMessage(
      // Clean view on → edit affordances off in the iframe, and vice versa.
      { enabled: !next, type: "setEditMode" },
      "*",
    );
  }, []);

  // Listen for messages from the iframe
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (!e.data?.type) {
        return;
      }

      if (e.data.type === "iframeScroll") {
        scrollPositionRef.current = e.data.scrollY;
      }

      if (e.data.type === "elementSelected") {
        if (e.data.editorKind === "templateText") {
          setSelectedElement({
            currentText: e.data.currentText,
            editableId: e.data.editableId,
            editorKind: "templateText",
            elementRect: e.data.elementRect,
            iframeScrollY: e.data.iframeScrollY,
            sectionId: e.data.sectionId,
            textMode: e.data.textMode,
          });
          return;
        }

        setSelectedElement({
          editorKind: "customAi",
          elementHtml: e.data.elementOuterHtml,
          elementPath: e.data.elementPath,
          elementRect: e.data.elementRect,
          iframeScrollY: e.data.iframeScrollY,
          sectionId: e.data.sectionId,
        });
      }

      if (e.data.type === "elementNotEditable") {
        setNotEditableToast(true);
        setTimeout(() => setNotEditableToast(false), 2000);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const handleIframeLoad = useCallback(() => {
    setIframeLoadCount((c) => c + 1);
    // Restore scroll position after reload
    const scrollY = scrollPositionRef.current;
    if (scrollY > 0) {
      requestAnimationFrame(() => {
        iframeRef.current?.contentWindow?.postMessage(
          { scrollY, type: "scrollRestore" },
          "*",
        );
      });
    }
    // Preview is editable by default; re-assert the current state after reload
    // so a toggled "Vista limpia" survives iframe refreshes.
    requestAnimationFrame(() => {
      iframeRef.current?.contentWindow?.postMessage(
        { enabled: !cleanViewRef.current, type: "setEditMode" },
        "*",
      );
    });
  }, []);

  const handleEditPopupClose = useCallback(() => {
    setSelectedElement(null);
    iframeRef.current?.contentWindow?.postMessage(
      { type: "clearSelection" },
      "*",
    );
  }, []);

  const handleTemplateTextUndo = useCallback(async () => {
    if (
      !site?._id ||
      isUndoingTemplateText ||
      !templateTextUndoState?.canUndo
    ) {
      return;
    }

    setIsUndoingTemplateText(true);
    handleEditPopupClose();
    try {
      await undoTemplateText({ siteId: site._id });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Template text undo failed:", error);
    } finally {
      setIsUndoingTemplateText(false);
    }
  }, [
    handleEditPopupClose,
    isUndoingTemplateText,
    site?._id,
    templateTextUndoState?.canUndo,
    undoTemplateText,
  ]);

  const [checkoutSuccess, setCheckoutSuccess] = useState(
    checkout === "success",
  );
  const [isPremiumBenefitsOpen, setIsPremiumBenefitsOpen] = useState(false);

  const handleSubscribe = useCallback(async () => {
    try {
      const returnUrl = window.location.href.split("?")[0];
      const { url } = await createCheckoutSession({
        country: localizedPrice?.country ?? undefined,
        returnUrl,
      });
      window.location.href = url;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to create checkout session:", error);
    }
  }, [createCheckoutSession, localizedPrice]);

  const handleManageSubscription = useCallback(async () => {
    try {
      const returnUrl = window.location.href.split("?")[0];
      const { url } = await createPortalSession({ returnUrl });
      window.location.href = url;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to create portal session:", error);
    }
  }, [createPortalSession]);

  const isSubscribed = subscriptionStatus?.subscriptionStatus === "active";
  const aiGenerationsUsed = subscriptionStatus?.aiGenerationsUsed ?? 0;
  const aiGenerationsDailyCount =
    subscriptionStatus?.aiGenerationsDailyCount ?? 0;
  const activeTab: DesktopTab =
    new URLSearchParams(location.search).get("tab") === "rsvp"
      ? "rsvp"
      : "edit";
  const [activeMobileTab, setActiveMobileTab] = useState<MobileTab>("preview");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    readSidebarCollapsedState,
  );
  const isDesktop = useIsDesktop();
  const inFlightSlug =
    pendingSlug?.fromSlug === routeSlug ? pendingSlug.slug : null;
  const currentSiteSlug = inFlightSlug ?? site?.slug ?? routeSlug;

  useEffect(() => {
    window.localStorage.setItem(
      SIDEBAR_COLLAPSED_STORAGE_KEY,
      String(isSidebarCollapsed),
    );
  }, [isSidebarCollapsed]);

  const handleSlugSaved = useCallback(
    async (nextSlug: string) => {
      setPendingSlug({ fromSlug: routeSlug, slug: nextSlug });
      if (typeof window !== "undefined") {
        window.location.replace(`/admin/${nextSlug}`);
        return;
      }

      await navigate({
        params: { slug: nextSlug },
        replace: true,
        to: "/admin/$slug",
      });
    },
    [navigate, routeSlug],
  );

  const closeOpenPopup = useCallback(() => {
    if (selectedElement !== null) {
      setSelectedElement(null);
      iframeRef.current?.contentWindow?.postMessage(
        { type: "clearSelection" },
        "*",
      );
    }
  }, [selectedElement]);

  const handleDesktopTabChange = useCallback(
    (tab: DesktopTab) => {
      if (tab !== "edit") {
        closeOpenPopup();
      }
    },
    [closeOpenPopup],
  );

  // Close any open edit popup when switching away from preview.
  const handleMobileTabChange = useCallback(
    (tab: MobileTab) => {
      setActiveMobileTab(tab);
      if (tab !== "preview") {
        closeOpenPopup();
      }
    },
    [closeOpenPopup],
  );

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--color-border)] border-t-[var(--color-accent)]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    navigate({
      search: { entry: undefined, flow: undefined, template: undefined },
      to: "/auth/signin",
    });
    return null;
  }

  if (site === undefined) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--color-border)] border-t-[var(--color-accent)]" />
        </div>
      </div>
    );
  }

  if (site === null && inFlightSlug && inFlightSlug !== routeSlug) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--color-border)] border-t-[var(--color-accent)]" />
        </div>
      </div>
    );
  }

  if (site === null) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="text-center py-16">
          <p className="text-gray-500">Sitio no encontrado</p>
        </div>
      </div>
    );
  }

  const siteUrl = `${getPublicSiteOrigin()}/love/${currentSiteSlug}`;

  const totalGuests =
    rsvps?.reduce(
      (sum: number, r: { attending: boolean; numberOfGuests: number }) =>
        r.attending ? sum + r.numberOfGuests : sum,
      0,
    ) ?? 0;
  const totalAttending =
    rsvps?.filter((r: { attending: boolean }) => r.attending).length ?? 0;
  const totalDeclined =
    rsvps?.filter((r: { attending: boolean }) => !r.attending).length ?? 0;

  return (
    <div className="h-[100dvh] flex flex-col bg-gray-50 overflow-hidden">
      <Header
        navContent={
          <DesktopHeaderTabs
            activeTab={activeTab}
            editHref={`/admin/${currentSiteSlug}?tab=edit`}
            onTabChange={handleDesktopTabChange}
            rsvpCount={totalAttending}
            rsvpHref={`/admin/${currentSiteSlug}?tab=rsvp`}
          />
        }
      />

      {/* Checkout success banner */}
      {checkoutSuccess && (
        <div className="shrink-0 bg-green-50 border-b border-green-200 px-4 py-2 flex items-center gap-3">
          <svg
            className="w-4 h-4 text-green-600 shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              d="M5 13l4 4L19 7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-sm text-green-700 font-medium">
            <Trans>
              ¡Suscripción activada! Ya puedes generar hasta 30 secciones con IA
              por día.
            </Trans>
          </span>
          <button
            className="ml-auto text-green-500 hover:text-green-700 cursor-pointer"
            onClick={() => setCheckoutSuccess(false)}
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Desktop layout ── */}
      <div className="hidden lg:flex flex-1 min-h-0">
        {activeTab === "rsvp" ? (
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full">
              <RsvpView
                isLimited={rsvpIsLimited}
                isSubscribed={isSubscribed}
                onShowPremiumBenefits={() => setIsPremiumBenefitsOpen(true)}
                rsvps={rsvps as RsvpEntry[] | undefined}
                siteSlug={currentSiteSlug}
                totalCount={rsvpTotalCount}
                totals={{ totalAttending, totalDeclined, totalGuests }}
              />
            </div>
          </div>
        ) : (
          <>
            {/* Sidebar */}
            <aside
              className={`relative shrink-0 border-r border-gray-200 bg-white transition-[width] duration-200 ${
                isSidebarCollapsed ? "w-14" : "w-80"
              }`}
              data-collapsed={isSidebarCollapsed}
              data-testid="desktop-sidebar"
            >
              {!isSidebarCollapsed && (
                <div className="h-full overflow-y-auto pb-12">
                  <AdminActionBar
                    currentSlug={currentSiteSlug}
                    onShowPremiumBenefits={() => setIsPremiumBenefitsOpen(true)}
                    onSlugSaved={handleSlugSaved}
                    showPremium={
                      !isSubscribed && subscriptionStatus !== undefined
                    }
                    siteId={site._id}
                    siteUrl={siteUrl}
                  />
                  <EditSidebar
                    aiGenerationsDailyCount={aiGenerationsDailyCount}
                    aiGenerationsUsed={aiGenerationsUsed}
                    currentOverrides={site.styleOverrides}
                    hasRenderablePhotos={hasRenderablePhotos}
                    iframeLoadCount={iframeLoadCount}
                    iframeRef={iframeRef}
                    isSubscribed={isSubscribed}
                    onManageSubscription={handleManageSubscription}
                    onSubscribe={handleSubscribe}
                    sectionConfig={site.sectionConfig}
                    siteId={site._id}
                    templateId={site.templateId}
                  />
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 border-t border-gray-200 bg-white/95 backdrop-blur-sm">
                <div className="flex items-center gap-2 px-4 py-1.5">
                  <button
                    aria-label={
                      isSidebarCollapsed
                        ? t`Abrir barra lateral`
                        : t`Cerrar barra lateral`
                    }
                    className="ml-auto flex h-7 w-7 items-center justify-center text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 cursor-pointer"
                    data-testid="sidebar-toggle"
                    onClick={() =>
                      setIsSidebarCollapsed((collapsed) => !collapsed)
                    }
                    title={
                      isSidebarCollapsed
                        ? t`Abrir barra lateral`
                        : t`Cerrar barra lateral`
                    }
                  >
                    {isSidebarCollapsed ? (
                      <PanelLeftOpen aria-hidden="true" className="h-4 w-4" />
                    ) : (
                      <PanelLeftClose aria-hidden="true" className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </aside>

            {/* Preview pane */}
            <div className="flex h-full min-h-0 flex-1 min-w-0 flex-col bg-gray-100">
              <div className="relative flex-1 min-h-0">
                {isDesktop && (
                  <iframe
                    onLoad={handleIframeLoad}
                    ref={iframeRef}
                    sandbox="allow-scripts allow-same-origin"
                    srcDoc={injectPreviewScript(site.generatedHtml)}
                    style={{ border: "none", height: "100%", width: "100%" }}
                    title="Vista previa"
                  />
                )}

                <PreviewEditMenu
                  canUndo={templateTextUndoState?.canUndo ?? false}
                  cleanView={cleanView}
                  isUndoing={isUndoingTemplateText}
                  onToggleCleanView={toggleCleanView}
                  onUndo={() => {
                    void handleTemplateTextUndo();
                  }}
                />

                {/* Not-editable toast */}
                {notEditableToast && (
                  <div
                    className="absolute top-4 left-1/2 -translate-x-1/2 z-40 px-4 py-2 bg-gray-700 text-white text-sm shadow-lg"
                    data-testid="template-edit-protected-toast"
                  >
                    <Trans>Este elemento no es editable</Trans>
                  </div>
                )}

                {/* Element edit popup */}
                {selectedElement?.editorKind === "customAi" && site._id && (
                  <ElementEditPopup
                    iframeLoadCount={iframeLoadCount}
                    onClose={handleEditPopupClose}
                    selectedElement={selectedElement}
                    siteId={site._id}
                  />
                )}
                {selectedElement?.editorKind === "templateText" && site._id && (
                  <TemplateTextEditPopup
                    key={`${selectedElement.sectionId}:${selectedElement.editableId}`}
                    onClose={handleEditPopupClose}
                    selectedText={selectedElement}
                    siteId={site._id}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Mobile layout ── */}
      <div className="lg:hidden flex-1 min-h-0 overflow-hidden">
        <div
          className={`flex w-full h-full min-h-0 flex-col bg-gray-100${activeMobileTab === "preview" ? "" : " hidden"}`}
        >
          <div className="relative flex-1 min-h-0">
            {!isDesktop && (
              <iframe
                onLoad={handleIframeLoad}
                ref={iframeRef}
                sandbox="allow-scripts allow-same-origin"
                srcDoc={injectPreviewScript(site.generatedHtml)}
                style={{ border: "none", height: "100%", width: "100%" }}
                title="Vista previa"
              />
            )}

            {activeMobileTab === "preview" && (
              <PreviewEditMenu
                canUndo={templateTextUndoState?.canUndo ?? false}
                cleanView={cleanView}
                isUndoing={isUndoingTemplateText}
                onToggleCleanView={toggleCleanView}
                onUndo={() => {
                  void handleTemplateTextUndo();
                }}
              />
            )}

            {/* Not-editable toast (mobile) */}
            {notEditableToast && activeMobileTab === "preview" && (
              <div
                className="absolute top-4 left-1/2 -translate-x-1/2 z-40 px-4 py-2 bg-gray-700 text-white text-sm shadow-lg"
                data-testid="template-edit-protected-toast"
              >
                <Trans>Este elemento no es editable</Trans>
              </div>
            )}

            {/* Element edit popup (mobile) */}
            {selectedElement?.editorKind === "customAi" &&
              site._id &&
              activeMobileTab === "preview" && (
                <ElementEditPopup
                  iframeLoadCount={iframeLoadCount}
                  onClose={handleEditPopupClose}
                  selectedElement={selectedElement}
                  siteId={site._id}
                />
              )}
            {selectedElement?.editorKind === "templateText" &&
              site._id &&
              activeMobileTab === "preview" && (
                <TemplateTextEditPopup
                  key={`${selectedElement.sectionId}:${selectedElement.editableId}`}
                  onClose={handleEditPopupClose}
                  selectedText={selectedElement}
                  siteId={site._id}
                />
              )}
          </div>
        </div>
        <div
          className={`overflow-y-auto h-full bg-white${activeMobileTab === "edit" ? "" : " hidden"}`}
          data-testid="mobile-edit-panel"
        >
          <AdminActionBar
            currentSlug={currentSiteSlug}
            onShowPremiumBenefits={() => setIsPremiumBenefitsOpen(true)}
            onSlugSaved={handleSlugSaved}
            showPremium={!isSubscribed && subscriptionStatus !== undefined}
            siteId={site._id}
            siteUrl={siteUrl}
          />
          <EditSidebar
            aiGenerationsDailyCount={aiGenerationsDailyCount}
            aiGenerationsUsed={aiGenerationsUsed}
            currentOverrides={site.styleOverrides}
            hasRenderablePhotos={hasRenderablePhotos}
            iframeLoadCount={iframeLoadCount}
            iframeRef={iframeRef}
            isSubscribed={isSubscribed}
            onManageSubscription={handleManageSubscription}
            onSubscribe={handleSubscribe}
            sectionConfig={site.sectionConfig}
            siteId={site._id}
            templateId={site.templateId}
          />
        </div>
        {activeMobileTab === "rsvp" && (
          <div
            className="overflow-y-auto h-full p-4"
            data-testid="mobile-rsvp-panel"
          >
            <RsvpView
              isLimited={rsvpIsLimited}
              isSubscribed={isSubscribed}
              onShowPremiumBenefits={() => setIsPremiumBenefitsOpen(true)}
              rsvps={rsvps as RsvpEntry[] | undefined}
              siteSlug={currentSiteSlug}
              totalCount={rsvpTotalCount}
              totals={{ totalAttending, totalDeclined, totalGuests }}
            />
          </div>
        )}
      </div>

      {/* Mobile bottom tab bar */}
      <MobileTabBar
        activeTab={activeMobileTab}
        onTabChange={handleMobileTabChange}
        rsvpCount={totalAttending}
      />

      {isPremiumBenefitsOpen && (
        <PremiumBenefitsPopup
          onClose={() => setIsPremiumBenefitsOpen(false)}
          onSubscribe={handleSubscribe}
        />
      )}
    </div>
  );
}
